import { defineFeature } from '../define-feature';
import { TEST_IDS, optionTestId, sidebarNavTestId } from '../test-ids';

export default defineFeature({
  id: 'agenda.appointment.create',
  category: 'agenda',
  title: 'Créer un rendez-vous',
  intent:
    'Bloquer un créneau de votre agenda pour un client, avec une ou plusieurs prestations et un ou plusieurs intervenants — que ce soit en saisissant vous-même le créneau ou en laissant le système chercher un créneau selon vos disponibilités.',
  context: {
    why: "Créer un rendez-vous manuellement dans votre agenda est l'une des actions les plus fréquentes de votre quotidien professionnel. Elle vous permet de centraliser dans un seul agenda tout ce qui ne vient pas de la réservation en ligne — un appel entrant, un client qui prend rendez-vous au comptoir en fin de séance, un rendez-vous pris par téléphone ou par messagerie. Sans cette centralisation, vous vous retrouvez rapidement avec des rendez-vous éparpillés entre différents supports (post-it, notes, agenda papier, messagerie), avec des risques de double-réservation ou d'oubli. La création manuelle de rendez-vous a été pensée pour être aussi rapide et sans erreur que possible. Plusieurs raccourcis sont à votre disposition : la sélection d'une prestation charge automatiquement sa durée, son prix et son lieu ; le mode « Selon mes dispos » trouve tout seul un créneau réellement libre à partir de la prestation choisie ; les raccourcis de durée (15 min, 30 min, 1 h, 2 h, toute la journée) accélèrent la saisie ; le glisser-déposer sur la grille permet de tracer un créneau directement sans passer par le formulaire. L'ensemble est conçu pour que la création d'un rendez-vous prenne quelques secondes plutôt que plusieurs minutes.",
    use_cases: [
      "Un client vous appelle pour prendre rendez-vous : vous créez le RDV à la main pendant l'appel, en quelques secondes.",
      'Vous cherchez rapidement un créneau libre pour une prestation donnée : utilisez « Selon mes dispos » qui vous propose les créneaux réellement disponibles.',
      'Un client vient de terminer sa séance et veut réserver la prochaine sur place : créez le RDV directement dans son agenda sans le faire attendre.',
      "Vous suivez un client en récurrent (kiné, coach) : configurez la répétition pour créer plusieurs rendez-vous d'un coup.",
      'Vous devez caler un rendez-vous long (2 heures ou plus) : utilisez les raccourcis de durée pour saisir vite.',
      'Vous proposez un soin à quatre mains ou avec assistant : ajoutez plusieurs intervenants sur le même rendez-vous.',
      'Vous voulez bloquer un créneau personnel non réservable en ligne (déjeuner, formation, imprévu) : utilisez le cadenas pour rendre le créneau non réservable.',
      'Un nouveau client se présente : vous créez son profil à la volée pendant la prise de rendez-vous, sans quitter le formulaire.',
    ],
  },
  required_scopes: ['agenda.appointment.create'],
  prerequisites: [
    {
      ref: 'customers.customer.create',
      requirement: 'recommended',
      why: 'Rattacher le RDV à un client (choisi dans la liste ou créé à la volée) évite les doublons et permet la notification — mais un rendez-vous peut être créé sans client.',
    },
    {
      ref: 'catalog.service.manage',
      requirement: 'optional',
      why: 'Sélectionner une prestation ajuste automatiquement la durée ; sans prestation, la durée se saisit manuellement via les champs début / fin.',
    },
  ],
  ui_entry_points: [
    { route: '/agenda', element: "Bouton « Ajouter » dans la barre d'outils de l'agenda" },
    { route: '/agenda', element: 'Clic sur un créneau vide dans la grille' },
  ],
  api: {
    primary_operation_id: 'AppointmentAppController_createAppointment',
    related_operation_ids: [
      'AvailabilityAppController_getAvailabilityCalendar',
      'ProCustomerAppController_getProCustomer',
      'PrestationAppController_getProPrestation',
    ],
  },
  steps: [
    "Ouvrir l'agenda et cliquer sur « Ajouter » pour ouvrir le formulaire de rendez-vous.",
    "Choisir la date et l'heure — manuellement ou via un créneau disponible.",
    "Sélectionner une ou plusieurs prestations : la durée du rendez-vous s'ajuste automatiquement. Chaque ligne porte son propre nombre de personnes, et le prix suit.",
    "Choisir le client (recherche par nom / téléphone, ou création à la volée) et le membre d'équipe.",
    'Valider — le rendez-vous apparaît dans la grille et le client reçoit la notification configurée.',
  ],
  gotchas: [
    'La fin proposée inclut le temps de battement de la prestation (ou celui propre au membre) et le repos entre rendez-vous : c’est la fin réellement bloquée dans l’agenda, le client voit une fin plus tôt. Le battement est rappelé sous le champ de fin et peut être retiré pour ce seul rendez-vous.',
    "Un membre de l'équipe est présélectionné (celui de la colonne, sinon le compte connecté) : l'enregistrement est refusé sans membre, sauf pour une prestation en libre-service.",
    'Avec plusieurs prestations, le rendez-vous est découpé en créneaux consécutifs — un par prestation — regroupés dans une seule réservation. Chaque créneau se modifie ou se retire ensuite indépendamment.',
    'Le stepper « Personnes » n’est pas plafonné par la capacité de la séance : la capacité est rappelée à titre indicatif, et seules les places réellement disponibles bloquent l’enregistrement.',
    'Une répétition regroupe au contraire toutes les prestations dans un seul créneau par occurrence.',
    "Si la synchronisation Google Calendar est active, l'événement est poussé en arrière-plan (visible sous ~5 s).",
    'Sans prestation sélectionnée, la durée doit être saisie manuellement via les champs début / fin.',
  ],
  related_features: ['customers.customer.create', 'catalog.service.manage'],
  demo_script: {
    duration_target_s: 45,
    steps: [
      {
        action: 'navigate',
        value: '/fr-FR',
        caption:
          'Un appel, un client au comptoir… Créer un rendez-vous à la main centralise tout dans un seul agenda, sans rien oublier.',
      },
      {
        action: 'click',
        target: sidebarNavTestId('/agenda'),
        caption: "Direction l'Agenda, dans le menu latéral.",
      },
      {
        action: 'click',
        target: TEST_IDS.agenda_add_appointment,
        caption: '« Ajouter » ouvre le formulaire d’un nouveau rendez-vous.',
      },
      {
        action: 'wait_for',
        target: TEST_IDS.appointment_name,
        caption: 'Le formulaire s’ouvre, prêt à être rempli en quelques secondes.',
      },
      {
        action: 'fill',
        target: TEST_IDS.appointment_name,
        value: 'Consultation découverte',
        caption: 'On donne un nom au rendez-vous pour le repérer d’un coup d’œil.',
        capture: true,
      },
      {
        action: 'expect_visible',
        target: TEST_IDS.appointment_submit,
        caption: 'Le bouton de validation est déjà là, à portée de clic.',
      },
      {
        action: 'click',
        target: TEST_IDS.prestation_picker,
        caption: 'Choisir une prestation charge automatiquement sa durée, son prix et son lieu.',
        capture: true,
      },
      {
        action: 'click',
        target: optionTestId(TEST_IDS.prestation_picker, 0),
        caption: 'La prestation sélectionnée s’ajoute au rendez-vous avec sa durée et son tarif.',
      },
      {
        action: 'click',
        target: TEST_IDS.prestation_party_size_increase,
        caption: 'Le nombre de personnes se règle ligne par ligne — le montant du rendez-vous suit.',
        capture: true,
      },
    ],
  },
});
